import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Settings2, Type, Palette, AlignLeft, Code2, Mail, Info,
  ChevronDown, Minus, Image as ImageIcon, MousePointerClick,
  Hash, FileText, LayoutGrid, Clock, Share2, Menu, Table2, Check,
  Upload, Link as LinkIcon,
} from 'lucide-react';
import { cn } from '../../utils';
import { useEditorStore } from '../../store';
import type { DesignElement } from '../../types/editor.types';
import { clickable } from "components/ui/interactive";

interface Props { element: DesignElement }

function Section({
  label, icon: Icon, children, defaultOpen = true,
}: {
  label: string; icon: React.ElementType; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-line-subtle dark:border-dark-700/60 overflow-hidden">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-2.5 px-3 py-2
          bg-gray-50/80 dark:bg-dark-900/50
          hover:bg-violet-50/60 dark:hover:bg-violet-900/10
          border-b border-line-subtle dark:border-dark-700/60 transition-all duration-150"
      >
        <div className="w-5 h-5 rounded-md
          bg-violet-100 dark:bg-violet-900/30
          flex items-center justify-center shrink-0">
          <Icon className="size-3 text-violet-600 dark:text-violet-400" />
        </div>
        <span className="flex-1 text-[10px] font-bold text-content-secondary dark:text-dark-300 text-left uppercase tracking-[0.1em]">
          {label}
        </span>
        <ChevronDown className={cn(
          'size-3.5 text-content-subtle dark:text-dark-500 transition-transform duration-200 shrink-0',
          !open && '-rotate-90'
        )} />
      </button>
      {open && (
        <div className="p-3 space-y-3 bg-surface dark:bg-dark-800">
          {children}
        </div>
      )}
    </div>
  );
}

/** Inline 2-col grid helper */
function Grid2({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>;
}

/** Color swatch + hex input row */
function ColorField({ label, value, onChange }: {
  label: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-dark-400">
        {label}
      </label>
      <div className="flex gap-2 items-center">
        <div className="relative shrink-0 group">
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-9 h-9 rounded-xl border-2 border-white dark:border-dark-600
              shadow-md cursor-pointer p-0.5 bg-transparent transition-all
              group-hover:scale-105 group-hover:shadow-lg"
          />
        </div>
        <input
          defaultValue={value}
          onBlur={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="flex-1 h-9 px-3 text-sm rounded-xl border border-line dark:border-dark-600
            bg-gray-50 dark:bg-dark-900/80 text-gray-800 dark:text-white
            font-mono tracking-wider
            focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400
            focus:bg-surface dark:focus:bg-dark-900 transition-all"
        />
      </div>
    </div>
  );
}

/** Inline mono textarea for code/HTML */
function CodeArea({ label, value, onSave, rows = 5 }: {
  label?: string; value: string; onSave: (v: string) => void; rows?: number;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-dark-400">
          {label}
        </label>
      )}
      <textarea
        defaultValue={value}
        onBlur={(e) => onSave(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 text-xs font-mono rounded-xl border resize-none
          border-gray-800 dark:border-dark-600
          bg-gray-900 dark:bg-black
          text-green-400 placeholder-gray-600
          focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-500
          transition-all leading-relaxed"
      />
    </div>
  );
}

/** Plain textarea (text content) */
function TextArea({ label, value, onSave, rows = 4 }: {
  label?: string; value: string; onSave: (v: string) => void; rows?: number;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-dark-400">
          {label}
        </label>
      )}
      <textarea
        defaultValue={value}
        onBlur={(e) => onSave(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 text-sm rounded-xl border resize-none
          border-line dark:border-dark-600
          bg-gray-50 dark:bg-dark-900/80
          text-gray-800 dark:text-white placeholder-gray-300 dark:placeholder-dark-600
          focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400
          focus:bg-surface dark:focus:bg-dark-900 transition-all"
      />
    </div>
  );
}

function SmartInput({
  label, value, onChange, placeholder, step = 1,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  step?: number;
}) {
  const [local, setLocal] = useState(value);
  const [_focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setLocal(value); }, [value]);

  const parse = (v: string) => {
    const m = v.trim().match(/^(-?[\d.]+)(px|em|rem|%|vw|vh|pt|cm|mm|deg|s|ms)?$/);
    return m ? { n: parseFloat(m[1]), u: m[2] ?? '' } : null;
  };

  const isNumericCSS = (v: string) =>
    parse(v) !== null || /(-?[\d.]+[a-z%]* ){1,}(-?[\d.]+[a-z%]*)/.test(v.trim());

  const nudge = useCallback((delta: number) => {
    // If field is empty, seed from placeholder (e.g. "0px")
    const base = local.trim() || placeholder?.trim() || '0px';
    const cursor = inputRef.current?.selectionStart ?? base.length;

    const tokens: { n: number; u: string; start: number; end: number }[] = [];
    const re = /(-?[\d.]+)([a-z%]*)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(base)) !== null) {
      tokens.push({ n: parseFloat(m[1]), u: m[2], start: m.index, end: m.index + m[0].length });
    }
    if (tokens.length === 0) return;

    let target = tokens[0];
    let minDist = Infinity;
    for (const t of tokens) {
      if (cursor >= t.start && cursor <= t.end) { target = t; minDist = 0; break; }
      const dist = Math.min(Math.abs(cursor - t.start), Math.abs(cursor - t.end));
      if (dist < minDist) { minDist = dist; target = t; }
    }

    const rounded = Math.round((target.n + delta) * 1000) / 1000;
    const replacement = `${rounded}${target.u}`;
    const next = base.slice(0, target.start) + replacement + base.slice(target.end);
    setLocal(next);
    onChange(next);

    const newCursor = target.start + replacement.length;
    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(newCursor, newCursor);
    });
  }, [local, placeholder, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocal(e.target.value);
    onChange(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const numericCSS = isNumericCSS(local) || local.trim() === '';
    if (e.key === 'ArrowUp')   { if (!numericCSS) return; e.preventDefault(); nudge(e.shiftKey ? 10 : step); }
    if (e.key === 'ArrowDown') { if (!numericCSS) return; e.preventDefault(); nudge(e.shiftKey ? -10 : -step); }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (document.activeElement !== e.currentTarget) return;
    if (!isNumericCSS(local) && local.trim() !== '') return;
    e.preventDefault();
    nudge(e.deltaY < 0 ? (e.shiftKey ? 10 : step) : (e.shiftKey ? -10 : -step));
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-dark-400">
          {label}
        </label>
      )}
      <div className="relative group/si">
        <input
          ref={inputRef}
          value={local}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onWheel={handleWheel}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder ?? '0px'}
          className="w-full h-9 pl-3 pr-6 text-sm rounded-xl border
            border-line dark:border-dark-600
            bg-gray-50 dark:bg-dark-900/80
            text-gray-800 dark:text-white
            focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400
            focus:bg-surface dark:focus:bg-dark-900
            transition-all"
        />

        {/* ▲▼ nudge buttons */}
        <div className="absolute right-1.5 inset-y-0 flex flex-col justify-center gap-[2px]
          opacity-0 group-hover/si:opacity-100 focus-within:opacity-100 transition-opacity
          pointer-events-none group-hover/si:pointer-events-auto focus-within:pointer-events-auto">
          <button tabIndex={-1}
            onMouseDown={(e) => { e.preventDefault(); nudge(step); inputRef.current?.focus(); }}
            className="flex items-center justify-center w-4 h-3.5 rounded
              text-gray-300 dark:text-dark-600
              hover:text-violet-500 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors">
            <svg width="6" height="4" viewBox="0 0 6 4" fill="currentColor"><path d="M3 0L6 4H0L3 0Z" /></svg>
          </button>
          <button tabIndex={-1}
            onMouseDown={(e) => { e.preventDefault(); nudge(-step); inputRef.current?.focus(); }}
            className="flex items-center justify-center w-4 h-3.5 rounded
              text-gray-300 dark:text-dark-600
              hover:text-violet-500 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors">
            <svg width="6" height="4" viewBox="0 0 6 4" fill="currentColor"><path d="M3 4L0 0H6L3 4Z" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/** Fully custom premium dropdown — portal-based so it always escapes overflow/scroll containers */
function PremiumSelect({
  label,
  value,
  onChange,
  options,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState<{ top: number; left: number; width: number; above: boolean } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const current = options.find((o) => o.value === value) ?? options[0];

  // Calculate portal position from trigger's screen rect
  const openDropdown = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < 180 && rect.top > 180; // flip up if tight
    setDropPos({
      top: above ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      above,
    });
    setOpen(true);
  };

  // Close on outside click or scroll
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', close, true);
    };
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open ? setOpen(false) : openDropdown(); return; }
    if (!open) return;
    const idx = options.findIndex((o) => o.value === value);
    if (e.key === 'ArrowDown') { e.preventDefault(); onChange(options[Math.min(idx + 1, options.length - 1)].value); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); onChange(options[Math.max(idx - 1, 0)].value); }
  };

  const dropdown = open && dropPos ? createPortal(
    <div
      onMouseDown={(e) => e.stopPropagation()} // prevent outside-click handler from firing on the panel itself
      style={{
        position: 'fixed',
        top: dropPos.above ? undefined : dropPos.top,
        bottom: dropPos.above ? window.innerHeight - dropPos.top : undefined,
        left: dropPos.left,
        width: dropPos.width,
        zIndex: 99999,
      }}
      className="rounded-xl border border-line-subtle dark:border-dark-700
        bg-surface dark:bg-dark-800
        shadow-2xl shadow-black/15 dark:shadow-black/50
        overflow-hidden"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(opt.value); setOpen(false); }}
          className={cn(
            'w-full flex items-center justify-between px-3 py-2 text-sm transition-colors',
            opt.value === value
              ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 font-semibold'
              : 'text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700/60'
          )}
        >
          {opt.label}
          {opt.value === value && <Check className="size-3 text-violet-500 dark:text-violet-400 shrink-0" />}
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-dark-400">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={open ? () => setOpen(false) : openDropdown}
          onKeyDown={handleKeyDown}
          className={cn(
            'w-full h-9 pl-3 pr-7 text-sm rounded-xl border text-left transition-all',
            'bg-gray-50 dark:bg-dark-900/80 text-gray-800 dark:text-white',
            open
              ? 'border-violet-400 ring-2 ring-violet-400/30 bg-surface dark:bg-dark-900'
              : 'border-line dark:border-dark-600 hover:border-violet-300 dark:hover:border-violet-700'
          )}
        >
          {current?.label}
        </button>
        <ChevronDown className={cn(
          'absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 pointer-events-none transition-transform duration-200',
          'text-content-subtle dark:text-dark-500',
          open && 'rotate-180'
        )} />
      </div>
      {dropdown}
    </div>
  );
}

/** Plain text / URL input — no numeric nudge, no arrow-key interception. */
function TextInput({
  label, value, onChange, placeholder, type = 'text',
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'url' | 'email';
}) {
  const [local, setLocal] = useState(value);
  // Keep in sync when value changes from outside (element switch handled by key={element.id})
  useEffect(() => { setLocal(value); }, [value]);

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-dark-400">
          {label}
        </label>
      )}
      <input
        type={type}
        value={local}
        onChange={(e) => { setLocal(e.target.value); onChange(e.target.value); }}
        placeholder={placeholder}
        className="w-full h-9 px-3 text-sm rounded-xl border
          border-line dark:border-dark-600
          bg-gray-50 dark:bg-dark-900/80
          text-gray-800 dark:text-white placeholder-gray-300 dark:placeholder-dark-600
          focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400
          focus:bg-surface dark:focus:bg-dark-900 transition-all"
      />
    </div>
  );
}

/** Image picker — drag-and-drop, click-to-upload, or paste a URL */
function ImageUploadField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [tab, setTab] = useState<'upload' | 'url'>('upload');

  const readFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (typeof e.target?.result === 'string') onChange(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) readFile(f);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) readFile(f);
  };

  const preview = value && (value.startsWith('data:') || value.startsWith('http') || value.startsWith('/'));

  return (
    <div className="space-y-2">
      {/* Tab switcher */}
      <div className="flex gap-1 p-0.5 rounded-lg bg-gray-100 dark:bg-dark-900">
        {([['upload', Upload, 'Upload File'], ['url', LinkIcon, 'Image URL']] as const).map(([id, Icon, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[11px] font-semibold transition-all',
              tab === id
                ? 'bg-surface dark:bg-dark-700 text-violet-600 dark:text-violet-400 shadow-sm'
                : 'text-content-subtle dark:text-dark-500 hover:text-content-secondary'
            )}
          >
            <Icon className="size-3" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'upload' ? (
        <>
          {/* Drop zone */}
          <div
            {...clickable(() => fileInputRef.current?.click())}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={onDrop}
            className={cn(
              'relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all',
              isDragOver
                ? 'border-violet-400 bg-violet-50 dark:bg-violet-900/20'
                : 'border-line dark:border-dark-600 hover:border-violet-300 dark:hover:border-violet-700 hover:bg-violet-50/40 dark:hover:bg-violet-900/10'
            )}
          >
            {preview ? (
              <>
                <img src={value} alt="preview" className="max-h-24 max-w-full rounded-lg object-contain shadow-sm" />
                <p className="text-[10px] text-content-subtle dark:text-dark-500">Click or drop to replace</p>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <Upload className="size-4 text-violet-500 dark:text-violet-400" />
                </div>
                <div className="text-center">
                  <p className="text-xs font-semibold text-content-secondary dark:text-dark-300">Click to upload</p>
                  <p className="text-[10px] text-content-subtle dark:text-dark-500 mt-0.5">or drag & drop · PNG, JPG, GIF, WebP, SVG</p>
                </div>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFileChange}
          />
        </>
      ) : (
        <div className="space-y-1.5">
          <input
            defaultValue={value.startsWith('data:') ? '' : value}
            onBlur={(e) => { if (e.target.value) onChange(e.target.value); }}
            placeholder="https://example.com/image.png"
            type="url"
            className="w-full h-9 px-3 text-sm rounded-xl border border-line dark:border-dark-600
              bg-gray-50 dark:bg-dark-900/80 text-gray-800 dark:text-white
              focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400
              focus:bg-surface dark:focus:bg-dark-900 transition-all"
          />
          {preview && !value.startsWith('data:') && (
            <img src={value} alt="preview" className="w-full max-h-24 rounded-lg object-contain bg-gray-50 dark:bg-dark-900" />
          )}
        </div>
      )}
    </div>
  );
}

function StyleTab({ element }: Props) {
  const { updateElementStyles } = useEditorStore();
  const s   = element.styles;
  const upd = (k: string, v: string) => updateElementStyles(element.id, { [k]: v });

  return (
    <div className="space-y-3">
      <Section label="Spacing" icon={Settings2}>
        <Grid2>
          <SmartInput label="Padding" value={(s.padding as string) ?? ''} onChange={(v) => upd('padding', v)} placeholder="16px" />
          <SmartInput label="Margin"  value={(s.margin  as string) ?? ''} onChange={(v) => upd('margin',  v)} placeholder="0px" />
        </Grid2>
      </Section>

      <Section label="Typography" icon={Type}>
        <Grid2>
          <SmartInput label="Font Size"   value={(s.fontSize   as string) ?? ''} onChange={(v) => upd('fontSize',   v)} placeholder="16px" />
          <SmartInput label="Line Height" value={(s.lineHeight as string) ?? ''} onChange={(v) => upd('lineHeight', v)} placeholder="1.6" step={0.1} />
          <PremiumSelect label="Weight" value={(s.fontWeight as string) ?? '400'} onChange={(v) => upd('fontWeight', v)}
            options={[
              { value:'300',label:'Light' }, { value:'400',label:'Regular' },
              { value:'500',label:'Medium'}, { value:'600',label:'SemiBold'},
              { value:'700',label:'Bold'  },
            ]} />
          <PremiumSelect label="Align" value={(s.textAlign as string) ?? 'left'} onChange={(v) => upd('textAlign', v)}
            options={[
              { value:'left',   label:'Left'   },
              { value:'center', label:'Center' },
              { value:'right',  label:'Right'  },
            ]} />
        </Grid2>
      </Section>

      <Section label="Colors" icon={Palette}>
        <ColorField label="Text Color"  value={(s.color           as string) ?? '#000000'} onChange={(v) => upd('color',           v)} />
        <ColorField label="Background"  value={(s.backgroundColor as string) ?? '#ffffff'} onChange={(v) => upd('backgroundColor', v)} />
      </Section>

      <Section label="Border" icon={Minus} defaultOpen={false}>
        <Grid2>
          <SmartInput label="Border Radius" value={(s.borderRadius as string) ?? ''} onChange={(v) => upd('borderRadius', v)} placeholder="0px" />
          <TextInput  label="Border"        value={(s.border       as string) ?? ''} onChange={(v) => upd('border',       v)} placeholder="1px solid #000" />
        </Grid2>
      </Section>
    </div>
  );
}

function ContentTab({ element }: Props) {
  const { updateElement, updateElementStyles } = useEditorStore();
  const upd  = (k: string, v: unknown) => updateElement(element.id, { content: { ...element.content, [k]: v } });
  const upds = (k: string, v: string)  => updateElementStyles(element.id, { [k]: v });

  /* BUTTON */
  if (element.type === 'button') return (
    <Section label="Button" icon={MousePointerClick}>
      <TextInput label="Label"    value={(element.content.text  as string) ?? ''} onChange={(v) => upd('text',  v)} />
      <TextInput label="Link URL" value={(element.content.href  as string) ?? ''} onChange={(v) => upd('href',  v)} placeholder="https://…" type="url" />
      <PremiumSelect label="Open In" value={(element.content.target as string) ?? '_blank'} onChange={(v) => upd('target', v)}
        options={[{ value:'_blank',label:'New Tab'},{ value:'_self',label:'Same Tab'}]} />
    </Section>
  );

  /* IMAGE */
  /* VIDEO */
  if (element.type === 'video') return (
    <Section label="Video" icon={FileText}>
      <TextInput label="Video URL"     value={(element.content.src       as string) ?? ''} onChange={(v) => upd('src',       v)} placeholder="https://youtube.com/…" type="url" />
      <TextInput label="Thumbnail URL" value={(element.content.thumbnail as string) ?? ''} onChange={(v) => upd('thumbnail', v)} placeholder="https://…" type="url" />
      <PremiumSelect label="Provider" value={(element.content.provider as string) ?? 'youtube'} onChange={(v) => upd('provider', v)}
        options={[{ value: 'youtube', label: 'YouTube' }, { value: 'vimeo', label: 'Vimeo' }]} />
    </Section>
  );

  if (element.type === 'image') return (
    <Section label="Image" icon={ImageIcon}>
      <ImageUploadField value={(element.content.src as string) ?? ''} onChange={(v) => upd('src', v)} />
      <TextInput label="Alt Text"  value={(element.content.alt  as string) ?? ''} onChange={(v) => upd('alt',  v)} />
      <TextInput label="Link URL"  value={(element.content.href as string) ?? ''} onChange={(v) => upd('href', v)} placeholder="https://…" type="url" />
      <SmartInput label="Width"    value={(element.content.width as string) ?? '100%'} onChange={(v) => upd('width', v)} placeholder="100%" />
    </Section>
  );

  /* HEADING */
  if (element.type === 'heading') return (
    <Section label="Heading" icon={Hash}>
      <TextArea label="Text" value={(element.content.text as string) ?? ''} onSave={(v) => upd('text', v)} rows={2} />
      <PremiumSelect label="Level" value={(element.content.level as string) ?? 'h2'} onChange={(v) => upd('level', v)}
        options={['h1','h2','h3','h4','h5','h6'].map((v) => ({ value:v, label:v.toUpperCase() }))} />
    </Section>
  );

  /* PARAGRAPH */
  if (element.type === 'paragraph') return (
    <Section label="Paragraph" icon={FileText}>
      <TextArea label="Text" value={(element.content.text as string) ?? ''} onSave={(v) => upd('text', v)} rows={4} />
    </Section>
  );

  /* TEXT / HTML inline */
  if (element.type === 'text') return (
    <Section label="Rich Text" icon={AlignLeft}>
      <p className="text-xs text-content-subtle dark:text-dark-500 leading-relaxed bg-gray-50 dark:bg-dark-900/60 rounded-lg px-3 py-2">
        Click the element on canvas to edit inline. You can also edit the raw HTML below.
      </p>
      <CodeArea label="Raw HTML" value={(element.content.html as string) ?? ''} onSave={(v) => upd('html', v)} rows={6} />
    </Section>
  );

  /* HTML block */
  if (element.type === 'html') return (
    <Section label="Custom HTML" icon={Code2}>
      <CodeArea value={(element.content.code as string) ?? ''} onSave={(v) => upd('code', v)} rows={9} />
    </Section>
  );

  /* DIVIDER */
  if (element.type === 'divider') return (
    <Section label="Divider" icon={Minus}>
      <Grid2>
        <SmartInput label="Thickness" value={(element.styles.borderTopWidth as string) ?? '1px'} onChange={(v) => upds('borderTopWidth', v)} placeholder="1px" />
        <PremiumSelect label="Style" value={(element.styles.borderTopStyle as string) ?? 'solid'} onChange={(v) => upds('borderTopStyle', v)}
          options={[{ value:'solid',label:'Solid'},{ value:'dashed',label:'Dashed'},{ value:'dotted',label:'Dotted'}]} />
      </Grid2>
      <ColorField label="Color" value={(element.styles.borderTopColor as string) ?? '#e2e8f0'} onChange={(v) => upds('borderTopColor', v)} />
    </Section>
  );

  /* COUNTDOWN */
  if (element.type === 'timer') {
    type L = { days:string; hours:string; minutes:string; seconds:string };
    const labels = (element.content.labels as L) ?? { days:'Days', hours:'Hours', minutes:'Minutes', seconds:'Seconds' };
    return (
      <div className="space-y-3">
        <Section label="Countdown" icon={Clock}>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-dark-400">End Date &amp; Time</label>
            <input type="datetime-local"
              defaultValue={(element.content.endDate as string)?.slice(0,16) ?? ''}
              onBlur={(e) => upd('endDate', new Date(e.target.value).toISOString())}
              className="w-full h-9 px-3 text-sm rounded-xl border border-line dark:border-dark-600
                bg-gray-50 dark:bg-dark-900/80 text-gray-800 dark:text-white
                focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 transition-all" />
          </div>
        </Section>
        <Section label="Unit Labels" icon={Type} defaultOpen={false}>
          <Grid2>
            {(['days','hours','minutes','seconds'] as const).map((u) => (
              <TextInput key={u} label={u.charAt(0).toUpperCase()+u.slice(1)} value={labels[u]}
                onChange={(v) => upd('labels', { ...labels, [u]: v })} />
            ))}
          </Grid2>
        </Section>
      </div>
    );
  }

  /* SOCIAL */
  if (element.type === 'social') {
    type P = { name:string; url:string };
    const platforms = (element.content.platforms as P[]) ?? [];
    return (
      <Section label="Social Links" icon={Share2}>
        <div className="space-y-2">
          {platforms.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase text-content-subtle w-14 shrink-0 capitalize">{p.name}</span>
              <TextInput value={p.url} placeholder="https://…" type="url" onChange={(v) => {
                const u = [...platforms]; u[i] = { ...p, url: v }; upd('platforms', u);
              }} />
            </div>
          ))}
        </div>
        <Grid2>
          <SmartInput label="Icon Size" value={String(element.content.iconSize ?? 32)} onChange={(v) => upd('iconSize', Number(v))} placeholder="32" />
          <PremiumSelect label="Layout" value={(element.content.layout as string) ?? 'horizontal'} onChange={(v) => upd('layout', v)}
            options={[{ value:'horizontal',label:'Horizontal'},{ value:'vertical',label:'Vertical'}]} />
        </Grid2>
      </Section>
    );
  }

  /* MENU */
  if (element.type === 'menu') {
    type MI = { label:string; href:string };
    const items = (element.content.items as MI[]) ?? [];
    return (
      <Section label="Navigation Menu" icon={Menu}>
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="grid grid-cols-2 gap-2">
              <TextInput placeholder="Label" value={item.label} onChange={(v) => {
                const u = [...items]; u[i] = { ...item, label: v }; upd('items', u);
              }} />
              <TextInput placeholder="URL" value={item.href} type="url" onChange={(v) => {
                const u = [...items]; u[i] = { ...item, href: v }; upd('items', u);
              }} />
            </div>
          ))}
        </div>
        <PremiumSelect label="Layout" value={(element.content.layout as string) ?? 'horizontal'} onChange={(v) => upd('layout', v)}
          options={[{ value:'horizontal',label:'Horizontal'},{ value:'vertical',label:'Vertical'}]} />
      </Section>
    );
  }

  /* TABLE */
  if (element.type === 'table') {
    const headers = (element.content.headers as string[]) ?? [];
    return (
      <Section label="Table" icon={Table2}>
        <div className="space-y-2">
          {headers.map((h, i) => (
            <TextInput key={i} label={`Column ${i+1}`} value={h} onChange={(v) => {
              const u = [...headers]; u[i] = v; upd('headers', u);
            }} />
          ))}
        </div>
        <p className="text-xs text-content-subtle dark:text-dark-500 bg-gray-50 dark:bg-dark-900/60 rounded-lg px-3 py-2">
          Edit cell data directly on the canvas.
        </p>
      </Section>
    );
  }

  /* FORM */
  if (element.type === 'form') {
    type Field = { id: string; type: string; label: string; placeholder: string; required?: boolean };
    const fields = (element.content.fields as Field[]) ?? [];
    return (
      <Section label="Form Fields" icon={FileText}>
        {fields.map((field, i) => (
          <div key={field.id} className="p-2 rounded-xl border border-line-subtle dark:border-dark-700 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-content-muted dark:text-dark-400 uppercase">Field {i + 1}</span>
              <PremiumSelect label="" value={field.type} onChange={(v) => {
                const updated = [...fields]; updated[i] = { ...field, type: v }; upd('fields', updated);
              }} options={[
                { value: 'text',     label: 'Text' },
                { value: 'email',    label: 'Email' },
                { value: 'tel',      label: 'Phone' },
                { value: 'number',   label: 'Number' },
                { value: 'textarea', label: 'Textarea' },
              ]} />
            </div>
            <TextInput label="Label"       value={field.label}       onChange={(v) => { const u = [...fields]; u[i] = { ...field, label: v };       upd('fields', u); }} />
            <TextInput label="Placeholder" value={field.placeholder} onChange={(v) => { const u = [...fields]; u[i] = { ...field, placeholder: v }; upd('fields', u); }} />
          </div>
        ))}
        <TextInput label="Submit Button Text" value={(element.content.submitText as string) ?? 'Submit'} onChange={(v) => upd('submitText', v)} />
        <TextInput label="Form Action URL"    value={(element.content.action     as string) ?? '#'}      onChange={(v) => upd('action',     v)} placeholder="https://…" type="url" />
      </Section>
    );
  }

  /* CAROUSEL */
  if (element.type === 'carousel') {
    type CarouselImage = { src: string; alt: string; href: string };
    const images = (element.content.images as CarouselImage[]) ?? [];
    return (
      <Section label="Carousel Images" icon={ImageIcon}>
        {images.map((img, i) => (
          <div key={i} className="p-2 rounded-xl border border-line-subtle dark:border-dark-700 space-y-1.5">
            <span className="text-[10px] font-semibold text-content-muted dark:text-dark-400">Slide {i + 1}</span>
            <TextInput label="Image URL" value={img.src} placeholder="https://…" onChange={(v) => {
              const updated = [...images]; updated[i] = { ...img, src: v }; upd('images', updated);
            }} type="url" />
            <TextInput label="Alt Text" value={img.alt} onChange={(v) => {
              const updated = [...images]; updated[i] = { ...img, alt: v }; upd('images', updated);
            }} />
          </div>
        ))}
      </Section>
    );
  }

  /* COLUMNS */
  if (element.type === 'columns') return (
    <Section label="Column Layout" icon={LayoutGrid}>
      <PremiumSelect label="Columns" value={String(element.content.columnCount ?? 2)} onChange={(v) => upd('columnCount', Number(v))}
        options={[{ value:'1',label:'1 Column'},{ value:'2',label:'2 Columns'},{ value:'3',label:'3 Columns'},{ value:'4',label:'4 Columns'}]} />
      <SmartInput label="Gap" value={(element.content.gap as string) ?? '16px'} onChange={(v) => upd('gap', v)} placeholder="16px" />
    </Section>
  );

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
      <Code2 className="size-10 text-gray-200 dark:text-dark-700" />
      <p className="text-sm text-content-subtle dark:text-dark-500">No content settings for this element.</p>
    </div>
  );
}

function RowSettings({ row }: { row: import('../../types/editor.types').DesignRow }) {
  const { updateRowStyles } = useEditorStore();
  const s = row.styles as Record<string, string>;
  const upd = (k: string, v: string) => updateRowStyles(row.id, { [k]: v });

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5 shrink-0
        bg-surface dark:bg-dark-800
        border-b border-line-subtle dark:border-dark-700/60">
        <div className="w-7 h-7 rounded-lg
          bg-violet-100 dark:bg-violet-900/30
          flex items-center justify-center shrink-0">
          <LayoutGrid className="size-3.5 text-violet-600 dark:text-violet-400" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold text-gray-800 dark:text-white leading-none">Row</p>
          <p className="text-[10px] text-content-subtle dark:text-dark-500 mt-0.5">Row properties</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {/* Background */}
        <Section label="Background" icon={Palette}>
          <ColorField
            label="Background Color"
            value={(s.backgroundColor as string) ?? '#ffffff'}
            onChange={(v) => upd('backgroundColor', v)}
          />
          <TextInput
            label="Background Image URL"
            value={(s.backgroundImage as string) ?? ''}
            onChange={(v) => upd('backgroundImage', v ? `url("${v}")` : '')}
            placeholder="https://…"
            type="url"
          />
          <Grid2>
            <TextInput label="BG Size"     value={(s.backgroundSize     as string) ?? ''} onChange={(v) => upd('backgroundSize',     v)} placeholder="cover" />
            <TextInput label="BG Position" value={(s.backgroundPosition as string) ?? ''} onChange={(v) => upd('backgroundPosition', v)} placeholder="center" />
          </Grid2>
        </Section>

        {/* Spacing */}
        <Section label="Spacing" icon={Settings2}>
          <Grid2>
            <SmartInput label="Padding" value={(s.padding as string) ?? ''} onChange={(v) => upd('padding', v)} placeholder="16px" />
            <SmartInput label="Margin"  value={(s.margin  as string) ?? ''} onChange={(v) => upd('margin',  v)} placeholder="0px" />
          </Grid2>
        </Section>

        {/* Border */}
        <Section label="Border" icon={Minus} defaultOpen={false}>
          <Grid2>
            <TextInput  label="Border"        value={(s.border       as string) ?? ''} onChange={(v) => upd('border',       v)} placeholder="1px solid #e5e7eb" />
            <SmartInput label="Border Radius" value={(s.borderRadius as string) ?? ''} onChange={(v) => upd('borderRadius', v)} placeholder="0px" />
          </Grid2>
        </Section>

        {/* Column count info */}
        <div className="rounded-xl border border-violet-100 dark:border-violet-800/20
          bg-violet-50/60 dark:bg-violet-900/10 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <div className="w-4 h-4 rounded-md bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Info className="size-2.5 text-violet-600 dark:text-violet-400" />
            </div>
            <p className="text-[10px] font-extrabold text-violet-600 dark:text-violet-400 uppercase tracking-[0.12em]">
              Row Info
            </p>
          </div>
          {[
            { label: 'Columns', value: String(row.columns.length) },
            { label: 'Elements', value: String(row.columns.reduce((a, c) => a + c.elements.length, 0)) },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-1.5
              border-b border-violet-100/80 dark:border-violet-800/20 last:border-0">
              <span className="text-[11px] text-violet-500/70 dark:text-violet-500">{label}</span>
              <span className="text-xs font-bold text-violet-700 dark:text-violet-300">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DesignSettings() {
  const { design, config } = useEditorStore();

  const updateBodyStyle = (key: string, value: string) =>
    useEditorStore.setState((s) => ({
      design: { ...s.design, body: { ...s.design.body, styles: { ...s.design.body.styles, [key]: value } } },
      isDirty: true,
    }));

  const updateMeta = (key: string, value: string) =>
    useEditorStore.setState((s) => ({
      design: { ...s.design, metadata: { ...s.design.metadata, [key]: value } },
      isDirty: true,
    }));

  const totalElements = design.body.rows.reduce(
    (acc, row) => acc + row.columns.reduce((a, c) => a + c.elements.length, 0), 0
  );

  return (
    <div className="flex flex-col h-full">
      {/* Compact clean header — consistent with LeftPanel */}
      <div className="flex items-center gap-2.5 px-3 py-2.5 shrink-0
        bg-surface dark:bg-dark-800
        border-b border-line-subtle dark:border-dark-700/60">
        <div className="w-7 h-7 rounded-lg
          bg-violet-100 dark:bg-violet-900/30
          flex items-center justify-center shrink-0">
          <Settings2 className="size-3.5 text-violet-600 dark:text-violet-400" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold text-gray-800 dark:text-white leading-none">Design Settings</p>
          <p className="text-[10px] text-content-subtle dark:text-dark-500 mt-0.5">Global canvas properties</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {/* Canvas */}
        <Section label="Canvas" icon={Palette}>
          <ColorField
            label="Background Color"
            value={(design.body.styles.backgroundColor as string) ?? '#ffffff'}
            onChange={(v) => updateBodyStyle('backgroundColor', v)}
          />
          <SmartInput
            label="Body Padding"
            value={(design.body.styles.padding as string) ?? '0px'}
            onChange={(v) => updateBodyStyle('padding', v)}
            placeholder="0px"
          />
        </Section>

        {/* Email metadata */}
        {config.mode === 'email' && (
          <Section label="Email Metadata" icon={Mail}>
            <TextInput
              label="Subject Line"
              value={design.metadata.subject ?? ''}
              onChange={(v) => updateMeta('subject', v)}
              placeholder="Your email subject…"
            />
            <TextInput
              label="Preheader Text"
              value={design.metadata.preheader ?? ''}
              onChange={(v) => updateMeta('preheader', v)}
              placeholder="Short preview text…"
            />
          </Section>
        )}

        {/* Design info card */}
        <div className="rounded-xl border border-violet-100 dark:border-violet-800/20
          bg-violet-50/60 dark:bg-violet-900/10 p-3">
          <div className="flex items-center gap-1.5 mb-2.5">
            <div className="w-4 h-4 rounded-md bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Info className="size-2.5 text-violet-600 dark:text-violet-400" />
            </div>
            <p className="text-[10px] font-extrabold text-violet-600 dark:text-violet-400 uppercase tracking-[0.12em]">
              Design Info
            </p>
          </div>
          {[
            { label: 'Mode',     value: config.mode.charAt(0).toUpperCase() + config.mode.slice(1) },
            { label: 'Rows',     value: String(design.body.rows.length) },
            { label: 'Elements', value: String(totalElements) },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-1.5
              border-b border-violet-100/80 dark:border-violet-800/20 last:border-0">
              <span className="text-[11px] text-violet-500/70 dark:text-violet-500">{label}</span>
              <span className="text-xs font-bold text-violet-700 dark:text-violet-300">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PropertiesPanel() {
  const { selectedElementId, selectedRowId, design } = useEditorStore();
  const [activeTab, setActiveTab] = useState<'content' | 'style'>('content');

  // Find selected element
  let element: DesignElement | null = null;
  for (const row of design.body.rows) {
    for (const col of row.columns) {
      const found = col.elements.find((e) => e.id === selectedElementId);
      if (found) { element = found; break; }
    }
    if (element) break;
  }

  // Row selected (no element) → show row properties
  if (!selectedElementId || !element) {
    const selectedRow = selectedRowId
      ? design.body.rows.find((r) => r.id === selectedRowId) ?? null
      : null;
    if (selectedRow) return <RowSettings row={selectedRow} />;
    return <DesignSettings />;
  }

  const typeLabel = element.type.charAt(0).toUpperCase() + element.type.slice(1);

  /* Element type → icon mapping */
  const TYPE_ICONS: Record<string, React.ElementType> = {
    button: MousePointerClick, image: ImageIcon, heading: Hash,
    paragraph: FileText, text: AlignLeft, html: Code2,
    divider: Minus, timer: Clock, social: Share2,
    menu: Menu, table: Table2, columns: LayoutGrid,
  };
  const HeaderIcon = TYPE_ICONS[element.type] ?? Settings2;

  return (
    <div className="flex flex-col h-full">
      {/* Compact structured header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5 shrink-0
        bg-surface dark:bg-dark-800
        border-b border-line-subtle dark:border-dark-700/60">
        <div className="w-7 h-7 rounded-lg
          bg-violet-100 dark:bg-violet-900/30
          flex items-center justify-center shrink-0">
          <HeaderIcon className="size-3.5 text-violet-600 dark:text-violet-400" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold text-gray-800 dark:text-white leading-none">{typeLabel}</p>
          <p className="text-[10px] text-content-subtle dark:text-dark-500 mt-0.5">Element properties</p>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex border-b border-line-subtle dark:border-dark-700/60
        bg-gray-50/50 dark:bg-dark-900/30">
        {(['content', 'style'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'flex-1 py-2.5 text-xs font-bold transition-all duration-150 relative',
              activeTab === tab
                ? 'text-violet-600 dark:text-violet-400 bg-surface dark:bg-dark-800'
                : 'text-content-subtle dark:text-dark-500 hover:text-gray-700 dark:hover:text-dark-300'
            )}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {activeTab === tab && (
              <span className="absolute bottom-0 left-3 right-3 h-[2px]
                bg-violet-500 dark:bg-violet-400 rounded-full" />
            )}
          </button>
        ))}
      </div>
      <div key={element.id} className="flex-1 overflow-y-auto px-3 py-3">
        {activeTab === 'content'
          ? <ContentTab element={element} />
          : <StyleTab element={element} />}
      </div>
    </div>
  );
}
